import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useLocation, useNavigate } from '@tanstack/react-router';
import { Command as CommandPrimitive } from 'cmdk';
import { useAtomValue, useSetAtom } from 'jotai';
import { Building, Check, CornerDownRight, Layers, Search } from 'lucide-react';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/new-ui/dialog';
import { currentTenantAtom } from '@/constants/currrentTenant';
import { findCurrentTenant } from '@/features/tenants/utils/find-tenant-with-parent';
import { countSelectableTenants } from '@/features/tenants/utils/selectable-tenants';
import { cn } from '@/lib/utils';
import { pushRecentTenantId } from '@/lib/recent-tenants';

export type TenantSwitcherDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type SwitchableTenant = { id: string; name: string };

/**
 * The current tenant, only when it is one of the user's tenants or sub-tenants. A tenant kept in
 * storage from another account is ignored, and a user with a single tenant falls back to it, the
 * same rule as `findCurrentTenant`. Shared by the whole shell.
 */
export const useSelectedTenant = () => {
  const { data: tenants } = useMyTenants();
  const currentTenant = useAtomValue(currentTenantAtom);

  const accessibleTenants = useMemo(
    () => tenants.flatMap((tenant) => [tenant, ...(tenant.subTenants ?? [])]),
    [tenants]
  );
  const selectedTenantId = findCurrentTenant(tenants, currentTenant?.id)?.id;
  const selectedTenant = selectedTenantId
    ? accessibleTenants.find((tenant) => tenant.id === selectedTenantId)
    : undefined;

  return { tenants, accessibleTenants, selectedTenant };
};

/** Everything the user menu and the dialog need to show and change the current tenant. */
export const useTenantSwitcher = () => {
  const { tenants, accessibleTenants, selectedTenant } = useSelectedTenant();
  const setCurrentTenant = useSetAtom(currentTenantAtom);
  const navigate = useNavigate();
  const location = useLocation();
  // The tenant root and every page below it
  const isUnderTenantPath = /^\/tenant\/[^/]+(\/|$)/.test(location.pathname);

  const me = useMe({ tenantId: selectedTenant?.id });
  // No selected tenant means "All tenants": data across every tenant the user can access
  const showAllTenantsOption = countSelectableTenants(tenants) > 0;

  /** Pass no tenant to switch to "All tenants". */
  const switchTenant = (tenant?: SwitchableTenant) => {
    if (isUnderTenantPath) {
      if (tenant) {
        navigate({ to: '/tenant/$tenantId/users', params: { tenantId: tenant.id } });
      } else {
        navigate({ to: '/estates' });
      }
    }
    setCurrentTenant(tenant ? { id: tenant.id, name: tenant.name } : undefined);
    if (tenant) pushRecentTenantId(tenant.id);
    toast.success('Updated the current tenant');
  };

  return { tenants, accessibleTenants, selectedTenant, me, showAllTenantsOption, switchTenant };
};

// Plain substring match: cmdk's default fuzzy scoring surfaces unrelated names for short queries
const filterBySubstring = (_value: string, search: string, keywords?: string[]) =>
  (keywords ?? []).some((k) => k.toLowerCase().includes(search.trim().toLowerCase())) ? 1 : 0;

const itemClass = cn(
  'group flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2.5 text-sm outline-none',
  'data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[disabled=true]:opacity-50'
);

/** Searchable tenant list opened from the user menu. */
export const TenantSwitcherDialog = ({ open, onOpenChange }: TenantSwitcherDialogProps) => {
  const { t } = useTranslation();
  const { tenants, selectedTenant, showAllTenantsOption, switchTenant } = useTenantSwitcher();

  const select = (tenant?: SwitchableTenant) => {
    onOpenChange(false);
    switchTenant(tenant);
  };

  const checkMark = (active: boolean) =>
    active ? <Check className="ml-auto h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="px-5 pb-3 pt-5 text-left">
          <DialogTitle className="text-base">{t('shellSwitchTenantTitle')}</DialogTitle>
          <DialogDescription className="text-[13px]">
            {t('shellSwitchTenantDescription')}
          </DialogDescription>
        </DialogHeader>

        <CommandPrimitive loop filter={filterBySubstring} className="flex flex-col">
          <div className="mx-5 mb-2 flex items-center gap-2 rounded-md border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <CommandPrimitive.Input
              autoFocus
              placeholder={t('shellSearchTenants')}
              className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <CommandPrimitive.List className="max-h-[min(420px,60vh)] overflow-y-auto overscroll-contain px-5 pb-3">
            {tenants.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('shellNoTenantsAssigned')}
              </p>
            ) : (
              <>
                <CommandPrimitive.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('shellNoTenantsFound')}
                </CommandPrimitive.Empty>

                {showAllTenantsOption && (
                  <CommandPrimitive.Group>
                    <CommandPrimitive.Item
                      value="__all__"
                      keywords={[t('allTenants')]}
                      onSelect={() => select()}
                      className={itemClass}
                    >
                      <Layers
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="flex min-w-0 flex-col">
                        <span
                          className={cn('truncate', !selectedTenant && 'font-medium text-primary')}
                        >
                          {t('allTenants')}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {t('shellAllTenantsDesc')}
                        </span>
                      </span>
                      {checkMark(!selectedTenant)}
                    </CommandPrimitive.Item>
                    <div className="my-1 h-px bg-border" />
                  </CommandPrimitive.Group>
                )}

                <CommandPrimitive.Group>
                  {tenants.map((tenant) => {
                    const subTenants = tenant.subTenants ?? [];
                    const tenantActive = tenant.id === selectedTenant?.id;
                    return (
                      <div key={tenant.id}>
                        <CommandPrimitive.Item
                          value={`tenant:${tenant.id}`}
                          keywords={[tenant.name]}
                          onSelect={() => select(tenant)}
                          className={itemClass}
                        >
                          <Building
                            className="h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                          <span
                            className={cn('truncate', tenantActive && 'font-medium text-primary')}
                          >
                            {tenant.name}
                          </span>
                          {subTenants.length > 0 && !tenantActive && (
                            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                              {t('shellSubTenantCount', { count: subTenants.length })}
                            </span>
                          )}
                          {checkMark(tenantActive)}
                        </CommandPrimitive.Item>

                        {subTenants.map((subTenant) => {
                          const subTenantActive = subTenant.id === selectedTenant?.id;
                          return (
                            <CommandPrimitive.Item
                              key={subTenant.id}
                              value={`branch:${subTenant.id}`}
                              // Searching a tenant name also surfaces its sub-tenants
                              keywords={[subTenant.name, tenant.name]}
                              onSelect={() => select(subTenant)}
                              className={cn(itemClass, 'pl-8')}
                            >
                              <CornerDownRight
                                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70"
                                aria-hidden="true"
                              />
                              <span
                                className={cn(
                                  'truncate',
                                  subTenantActive && 'font-medium text-primary'
                                )}
                              >
                                {subTenant.name}
                              </span>
                              {checkMark(subTenantActive)}
                            </CommandPrimitive.Item>
                          );
                        })}
                      </div>
                    );
                  })}
                </CommandPrimitive.Group>
              </>
            )}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
};
